import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";

import Button from "components/Button";
import HollowButton from "components/HollowButton";
import ScanDocumentModal from "components/ScanDocumentModal";

import parcelService from "api/parcelService";
import ModalsManagerContext from "contexts/ModalsManager";
import { getCompanyPublicKeyResult } from "utils/encryptForCompany";
import { IPackage } from "dto/IPackage";
import {
  EIdDocumentType,
  ID_DOCUMENT_TYPE_LABELS,
} from "dto/Mailroom/IPickup";
import { handleCreate } from "utils/uploadFile";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";

interface ScanFile {
  uri: string;
  keyId: string | null;
  fileName: string;
}

/**
 * Zadanie 1 — „Potwierdź odbiór osobisty": rejestracja odbioru przesyłki
 * WYCHODZĄCEJ o formie doręczenia „Odbiór osobisty" (PersonalPickup — adresat
 * odbiera osobiście przy okienku). Port formatki z Magistratu
 * (Kwantum.Parcels.UI/DirectDispatch): data odbioru + kto odebrał (imię/nazwisko
 * + dokument tożsamości) + „nie odebrano" + skan/plik potwierdzenia (dowód).
 * Uruchamiana z OptionsCard (localStorage("package")).
 */
const ConfirmPickupModal = () => {
  const { modals, setModals } = useContext(ModalsManagerContext);
  const queryClient = useQueryClient();

  const pack = useMemo<IPackage | null>(() => {
    try {
      const raw = localStorage.getItem("package");
      return raw ? (JSON.parse(raw) as IPackage) : null;
    } catch {
      return null;
    }
  }, []);

  const { mutate, isLoading } = parcelService.useConfirmOutgoingPickup();
  const { data: infoData } = parcelService.useGetOutgoingPickupInfo(
    pack?.Id ?? "",
  );

  const [notCollected, setNotCollected] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [idType, setIdType] = useState<number | null>(null);
  const [idNumber, setIdNumber] = useState("");
  const [date, setDate] = useState<string>(
    new Date().toLocaleDateString("sv-SE"),
  );
  const [scan, setScan] = useState<ScanFile | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showScan, setShowScan] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Prefill istniejącym potwierdzeniem (jeśli już zarejestrowano wcześniej).
  const info = infoData?.data;
  useEffect(() => {
    if (!info?.IsConfirmed) return;
    setNotCollected(info.NotCollected);
    setFirstName(info.FirstName ?? "");
    setLastName(info.LastName ?? "");
    setIdType(info.IdDocumentType ?? null);
    setIdNumber(info.IdDocumentNumber ?? "");
    if (info.PickupDate) {
      setDate(new Date(info.PickupDate).toLocaleDateString("sv-SE"));
    }
    if (info.HasConfirmationScan && info.ScanFileUri) {
      setScan({
        uri: info.ScanFileUri,
        keyId: info.ScanKeyId ?? null,
        fileName: "potwierdzenie.pdf",
      });
    }
  }, [info]);

  const close = () => setModals({ ...modals, confirmPickup: false });

  // Skan/plik potwierdzenia — szyfrowany kluczem publicznym profilu.
  const uploadScan = async (file: File) => {
    setUploading(true);
    try {
      // Skan szyfrowany kluczem publicznym FIRMY (model kluczy firmowych) — RZUCA
      // (CompanyKeyMissingError), gdy firma nie ma jeszcze aktywnego klucza.
      const masterKey = await getCompanyPublicKeyResult();
      const r = await handleCreate(
        [file],
        undefined,
        masterKey,
        true,
        true,
        file.name,
        file.type || "application/pdf",
      );
      if (!r || !r[0]) throw new Error("Upload nie zwrócił pliku.");
      setScan({ uri: r[0].uri, keyId: r[0].keyId ?? null, fileName: file.name });
    } catch (e) {
      notification({
        type: ENotificationType.ERROR,
        title: "Błąd",
        text: (e as Error)?.message ?? "Nie udało się dołączyć skanu.",
      });
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) uploadScan(file);
  };

  const canSubmit =
    !uploading &&
    date !== "" &&
    (notCollected || (firstName.trim() !== "" && lastName.trim() !== "")) &&
    (idType == null || idNumber.trim() !== "");

  const submit = () => {
    if (!pack) return;
    mutate(
      {
        ParcelId: pack.Id,
        PickupDate: new Date(date).toISOString(),
        NotCollected: notCollected,
        FirstName: notCollected ? null : firstName.trim() || null,
        LastName: notCollected ? null : lastName.trim() || null,
        IdDocumentType: notCollected ? null : idType,
        IdDocumentNumber: notCollected ? null : idNumber.trim() || null,
        ScanFileUri: scan?.uri ?? null,
        ScanKeyId: scan?.keyId ?? null,
      },
      {
        onSuccess: () => {
          notification({
            type: ENotificationType.SUCCESS,
            title: notCollected
              ? "Zarejestrowano brak odbioru"
              : "Potwierdzono odbiór osobisty",
            text: `Przesyłka ${pack.ParcelNumber ?? ""} zaktualizowana.`,
          });
          queryClient.invalidateQueries("packages");
          queryClient.invalidateQueries(["outgoingParcel.pickup", pack.Id]);
          close();
        },
        onError: () =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: "Nie udało się zapisać potwierdzenia odbioru.",
          }),
      },
    );
  };

  // Modal montowany tylko po zapisie localStorage("package") w OptionsCard —
  // brak paczki to stan zdegenerowany; renderujemy pusto (bez setState w renderze).
  if (!pack) return null;

  return (
    <>
      <div className={styles.overlay} onMouseDown={close}>
        <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
          <div className={styles.modalTitle}>Potwierdź odbiór osobisty</div>
          <div className={styles.modalBody}>
            <div className={styles.modalRow}>
              <span>Przesyłka:</span>
              <strong>
                {pack.ParcelNumber ??
                  pack.S1?.replace(/<\/?em>/g, "") ??
                  "—"}
              </strong>
            </div>

            <label className={styles.check}>
              <input
                type="checkbox"
                checked={notCollected}
                onChange={(e) => setNotCollected(e.target.checked)}
              />
              Przesyłki nie odebrano
            </label>

            <div className={styles.field}>
              <label>Data odbioru</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>

            {!notCollected && (
              <>
                <div className={styles.row2}>
                  <div className={styles.field}>
                    <label>Imię</label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </div>
                  <div className={styles.field}>
                    <label>Nazwisko</label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>
                </div>

                <div className={styles.row2}>
                  <div className={styles.field}>
                    <label>Rodzaj dokumentu</label>
                    <select
                      value={idType ?? ""}
                      onChange={(e) => {
                        const v =
                          e.target.value === "" ? null : Number(e.target.value);
                        setIdType(v);
                        if (v == null) setIdNumber("");
                      }}
                    >
                      <option value="">— brak —</option>
                      {Object.values(EIdDocumentType)
                        .filter((v) => typeof v === "number")
                        .map((v) => (
                          <option key={v} value={v as number}>
                            {ID_DOCUMENT_TYPE_LABELS[v as EIdDocumentType]}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className={styles.field}>
                    <label>Numer dokumentu</label>
                    <input
                      type="text"
                      value={idNumber}
                      disabled={idType == null}
                      onChange={(e) => setIdNumber(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}

            <div className={styles.field}>
              <label>Skan / plik potwierdzenia (dowód)</label>
              {scan ? (
                <div className={styles.noteRow}>
                  <span className={styles.noteName}>{scan.fileName}</span>
                  <HollowButton text="Usuń" onClick={() => setScan(null)} />
                </div>
              ) : (
                <div className={styles.noteActions}>
                  <HollowButton
                    text={uploading ? "Wgrywanie…" : "Wgraj plik"}
                    onClick={() => fileInputRef.current?.click()}
                  />
                  <HollowButton text="Skanuj" onClick={() => setShowScan(true)} />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf,image/*"
                    hidden
                    onChange={onPickFile}
                  />
                </div>
              )}
            </div>
          </div>
          <div className={styles.modalFooter}>
            <HollowButton text="Anuluj" onClick={close} />
            <Button
              text="Zapisz"
              onClick={submit}
              loading={isLoading}
              disabled={!canSubmit}
            />
          </div>
        </div>
      </div>

      {showScan && (
        <ScanDocumentModal
          defaultFileName={`odbior-${(pack.ParcelNumber ?? pack.Id).replace(
            /\//g,
            "-",
          )}`}
          handleClose={() => setShowScan(false)}
          onComplete={(pdf) => uploadScan(pdf)}
        />
      )}
    </>
  );
};

export default ConfirmPickupModal;
